import { useState } from "react";
import { useAuth } from "@/App";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ChangePasswordPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!user) return <Navigate to="/login" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError("New password must be at least 8 characters"); return; }
    if (next !== confirm) { setError("New password and confirmation do not match"); return; }
    if (next === current) { setError("New password must differ from current"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setUser({ ...user, must_change_password: false });
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="change-password-page">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center mx-auto">
            <img src="/logo-mark.png" alt="Arrivo" className="w-14 h-14 object-contain" />
          </div>
          <h1 className="text-3xl tracking-tight font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>
            ARRIVO
          </h1>
          <p className="text-sm text-muted-foreground">
            {user.must_change_password ? "Set your password to continue" : "Change your password"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-sm text-red-400" data-testid="change-password-error">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="current" className="text-sm text-muted-foreground">
              {user.must_change_password ? "Temporary password" : "Current password"}
            </Label>
            <Input
              id="current" type="password" value={current}
              onChange={(e) => setCurrent(e.target.value)} required autoFocus
              className="bg-card border-border" data-testid="current-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="next" className="text-sm text-muted-foreground">New password</Label>
            <Input
              id="next" type="password" value={next}
              onChange={(e) => setNext(e.target.value)} required
              placeholder="At least 8 characters"
              className="bg-card border-border" data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm" className="text-sm text-muted-foreground">Confirm new password</Label>
            <Input
              id="confirm" type="password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} required
              className="bg-card border-border" data-testid="confirm-password-input"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="change-password-submit">
            {loading ? "Updating..." : "Set Password"}
          </Button>
          {!user.must_change_password && (
            <Button type="button" variant="ghost" className="w-full" onClick={() => navigate(-1)} data-testid="change-password-cancel">
              Cancel
            </Button>
          )}
          {user.must_change_password && (
            <button type="button" onClick={logout} className="w-full text-xs text-muted-foreground hover:text-foreground" data-testid="change-password-logout">
              Sign out
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
